import { useEffect, useState, useRef } from 'react'

type AnimatedTextPlaceholderProps = {
  phrase?: string
  typingSpeed?: number
  loop?: boolean
  maxLoops?: number
  pauseTime?: number
  enabled?: boolean
}

export function useAnimatedTextPlaceholder({
  phrase = 'Search...',
  typingSpeed = 60,
  loop = true,
  maxLoops = 3,
  pauseTime = 1000,
  enabled = true,
}: AnimatedTextPlaceholderProps): string {
  const [placeholder, setPlaceholder] = useState('')
  const animationStateRef = useRef({
    currentIndex: 0,
    loopCount: 0,
    timeoutId: null as ReturnType<typeof setTimeout> | null,
  })

  useEffect(() => {
    if (!enabled || !phrase.trim()) return

    const state = animationStateRef.current
    state.currentIndex = 0
    state.loopCount = 0
    setPlaceholder('')

    if (state.timeoutId) {
      clearTimeout(state.timeoutId)
      state.timeoutId = null
    }

    const animateCharacters = () => {
      if (state.currentIndex < phrase.length) {
        const nextIndex = phrase[state.currentIndex]
        state.timeoutId = setTimeout(() => {
          if (!state.timeoutId) return
          setPlaceholder(prev => prev + nextIndex)
          state.currentIndex += 1
          animateCharacters()
        }, typingSpeed)
      } else if (loop && state.loopCount < maxLoops - 1) {
        state.timeoutId = setTimeout(() => {
          if (!state.timeoutId) return
          setPlaceholder('')
          state.currentIndex = 0
          state.loopCount += 1
          animateCharacters()
        }, pauseTime)
      }
    }

    animateCharacters()

    return () => {
      clearTimeout(state.timeoutId)
      state.timeoutId = null
    }
  }, [phrase, typingSpeed, loop, pauseTime, maxLoops, enabled])

  if (!enabled) return phrase
  return placeholder
}
